import { EVENT_SCOPES, WEEKDAYS, type EventCreate, type EventScope, type EventUpdate, type EventView, type Recurrence, type Weekday } from "@confluence/shared";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useId, useMemo, useRef, useState, type FormEvent } from "react";
import { ApiFailure, del, get, patch, post } from "../api";
import { useI18n } from "../i18n";
import { type CalendarInfo } from "./data";
import { dayFmt, timeFmt } from "./format";
import { PlaceField } from "./places";
import { addDays, dayKeyIn, minutesIn, weekday, zonedInstant, type DayKey } from "./time";

/** Un agenda accepte l'écriture s'il est modifiable et n'est pas un abonnement iCal. */
export const canWrite = (cal: CalendarInfo | undefined) => !!cal && cal.writable && cal.connection.provider !== "ics";

export type EditorTarget = { kind: "new"; date: DayKey; minutes: number | null; allDay?: boolean } | { kind: "edit"; ev: EventView };

type Repeat = "none" | "daily" | "weekly" | "monthly" | "yearly";
interface Draft {
  calendarId: string;
  title: string;
  allDay: boolean;
  startDate: DayKey;
  startTime: string;
  endDate: DayKey;
  endTime: string;
  location: string;
  description: string;
  attendees: string[];
  transparency: "opaque" | "transparent";
  isPrivate: boolean;
  meeting: boolean;
  repeat: Repeat;
  interval: number;
  byDay: Weekday[];
  ends: "never" | "until" | "count";
  until: DayKey;
  count: number;
  /** "" = réglage par défaut de l'agenda */
  reminder: string;
}

const hhmm = (min: number) => `${String(Math.floor(min / 60) % 24).padStart(2, "0")}:${String(min % 60).padStart(2, "0")}`;
const toMin = (t: string) => {
  const [h, m] = t.split(":").map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
};
const weekdayOf = (key: DayKey): Weekday => WEEKDAYS[(weekday(key) + 6) % 7]!;

function initialDraft(target: EditorTarget, calendars: CalendarInfo[], tz: string): Draft {
  const blank = {
    location: "",
    description: "",
    attendees: [] as string[],
    transparency: "opaque" as const,
    isPrivate: false,
    meeting: false,
    repeat: "none" as Repeat,
    interval: 1,
    ends: "never" as const,
    count: 10,
    reminder: "",
  };
  if (target.kind === "new") {
    const cal = calendars.find((c) => c.isDefault) ?? calendars[0];
    const start = target.minutes ?? 9 * 60;
    const end = Math.min(start + 60, 24 * 60 - 15);
    return {
      ...blank,
      calendarId: cal?.id ?? "",
      title: "",
      allDay: !!target.allDay,
      startDate: target.date,
      startTime: hhmm(start),
      endDate: target.date,
      endTime: hhmm(end),
      byDay: [weekdayOf(target.date)],
      until: addDays(target.date, 90),
    };
  }
  const ev = target.ev;
  const startDate = ev.allDay ? ev.start.slice(0, 10) : dayKeyIn(ev.start, tz);
  const endDate = ev.allDay ? addDays(ev.end.slice(0, 10), -1) : dayKeyIn(new Date(ev.end).getTime() - 1, tz);
  return {
    ...blank,
    calendarId: ev.calendarId,
    title: ev.title,
    allDay: ev.allDay,
    startDate,
    startTime: ev.allDay ? "09:00" : hhmm(minutesIn(ev.start, tz)),
    endDate: endDate < startDate ? startDate : endDate,
    endTime: ev.allDay ? "10:00" : hhmm(minutesIn(ev.end, tz)),
    location: ev.location ?? "",
    description: ev.description ?? "",
    attendees: ev.attendees.filter((a) => !a.organizer).map((a) => a.email),
    transparency: ev.transparency,
    isPrivate: ev.isPrivate,
    byDay: [weekdayOf(startDate)],
    until: addDays(startDate, 90),
  };
}

/** Début et fin envoyés à l'API : instants ISO, ou dates (fin exclusive) pour une journée entière. */
function times(d: Draft, tz: string) {
  if (d.allDay) return { allDay: true, start: d.startDate, end: addDays(d.endDate, 1) };
  return {
    allDay: false,
    start: zonedInstant(d.startDate, toMin(d.startTime), tz).toISOString(),
    end: zonedInstant(d.endDate, toMin(d.endTime), tz).toISOString(),
  };
}

function payload(d: Draft, tz: string): Omit<EventCreate, "calendarId"> {
  return {
    title: d.title.trim(),
    ...times(d, tz),
    timezone: tz,
    location: d.location.trim() || null,
    description: d.description.trim() || null,
    attendees: d.attendees,
    transparency: d.transparency,
    isPrivate: d.isPrivate,
    meeting: d.meeting,
    recurrence:
      d.repeat === "none"
        ? null
        : {
            freq: d.repeat,
            interval: d.interval,
            ...(d.repeat === "weekly" ? { byDay: d.byDay } : {}),
            ...(d.ends === "until" ? { until: d.until } : d.ends === "count" ? { count: d.count } : {}),
          },
    reminderMinutes: d.reminder === "" ? null : Number(d.reminder),
  };
}

/** Modification : seuls les champs qui ont changé sont envoyés. */
function changes(before: Draft, after: Draft, tz: string): EventUpdate {
  const a = payload(before, tz);
  const b = payload(after, tz);
  const out: Record<string, unknown> = { timezone: tz };
  const same = (x: unknown, y: unknown) => JSON.stringify(x) === JSON.stringify(y);
  if (!same([a.allDay, a.start, a.end], [b.allDay, b.start, b.end])) Object.assign(out, { allDay: b.allDay, start: b.start, end: b.end });
  for (const k of ["title", "location", "description", "attendees", "transparency", "isPrivate", "meeting", "recurrence"] as const) {
    if (!same(a[k], b[k])) out[k] = b[k];
  }
  if (before.reminder !== after.reminder) out.reminderMinutes = b.reminderMinutes;
  return out as EventUpdate;
}

/** Message d'erreur d'une écriture, avec le détail du fournisseur quand il explique le refus. */
export function writeErrorText(err: unknown, errorText: (e: unknown) => string) {
  const base = errorText(err);
  return err instanceof ApiFailure && err.code === "EVENT_REJECTED" && err.message ? `${base} ${err.message}` : base;
}

export function EventEditor({
  target,
  calendars,
  tz,
  onClose,
  onDone,
}: {
  target: EditorTarget;
  calendars: Map<string, CalendarInfo>;
  tz: string;
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  const { t, locale, errorText } = useI18n();
  const qc = useQueryClient();
  const ref = useRef<HTMLDialogElement>(null);
  // Agendas proposés : modifiables et affichés (activés dans les réglages), l'agenda par défaut en premier.
  const writable = useMemo(
    () =>
      [...calendars.values()]
        .filter((c) => canWrite(c) && (c.shown || (target.kind === "edit" && c.id === target.ev.calendarId)))
        .sort((a, b) => Number(b.isDefault) - Number(a.isDefault)),
    [calendars],
  );
  const [initial, setInitial] = useState(() => initialDraft(target, writable, tz));
  const [d, setD] = useState(initial);
  const [scope, setScope] = useState<EventScope>("this");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const editing = target.kind === "edit" ? target.ev : null;
  // Série : sa règle actuelle, pour la modifier (toute la série ou à partir de cette occurrence).
  const rule = useQuery({
    queryKey: ["recurrence", editing?.id],
    queryFn: () => get<{ recurrence: Recurrence | null; editable: boolean }>(`/api/events/${editing!.id}/recurrence`),
    enabled: !!editing?.recurring,
    staleTime: 60_000,
  });
  useEffect(() => {
    const r = rule.data?.recurrence;
    if (!r) return;
    const fields = {
      repeat: r.freq,
      interval: r.interval,
      byDay: r.byDay?.length ? r.byDay : initial.byDay,
      ends: r.until ? ("until" as const) : r.count ? ("count" as const) : ("never" as const),
      until: r.until ?? initial.until,
      count: r.count ?? initial.count,
    };
    setInitial((x) => ({ ...x, ...fields }));
    setD((x) => ({ ...x, ...fields }));
  }, [rule.data]);
  const cal = calendars.get(d.calendarId);
  const provider = cal?.connection.provider;
  const id = useId();

  useEffect(() => {
    ref.current?.showModal();
  }, []);

  const set = <K extends keyof Draft>(key: K, value: Draft[K]) => setD((x) => ({ ...x, [key]: value }));
  // Changer le début déplace la fin d'autant : la durée est conservée.
  const moveStart = (date: DayKey, time: string) =>
    setD((x) => {
      if (x.allDay) return { ...x, startDate: date, endDate: addDays(date, Math.max(0, daysDiff(x.startDate, x.endDate))) };
      const before = zonedInstant(x.startDate, toMin(x.startTime), tz).getTime();
      const after = zonedInstant(date, toMin(time), tz).getTime();
      const end = new Date(zonedInstant(x.endDate, toMin(x.endTime), tz).getTime() + (after - before));
      return { ...x, startDate: date, startTime: time, endDate: dayKeyIn(end, tz), endTime: hhmm(minutesIn(end, tz)), byDay: x.repeat === "weekly" && x.byDay.length === 1 ? [weekdayOf(date)] : x.byDay };
    });

  const t0 = times(d, tz);
  const invalid = new Date(t0.end) <= new Date(t0.start);
  const conflicts = useConflicts(t0, editing?.id, calendars, d.transparency === "transparent");

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (invalid || !d.calendarId) return;
    setPending(true);
    setError(null);
    try {
      if (editing) {
        const body = changes(initial, d, tz);
        // Une occurrence seule ne change pas la règle de la série.
        if (scope === "this" || !rule.data?.editable) delete body.recurrence;
        await patch(`/api/events/${editing.id}`, { ...body, scope: editing.recurring ? scope : "this" });
      } else {
        await post("/api/events", { ...payload(d, tz), calendarId: d.calendarId });
      }
      await qc.invalidateQueries({ queryKey: ["events"] });
      onDone(t("ev.saved"));
    } catch (err) {
      setError(writeErrorText(err, errorText));
      setPending(false);
    }
  };

  return (
    <dialog ref={ref} className="ev-dialog ev-editor" onClose={onClose} aria-labelledby={`${id}-h`}>
      <form className="ev-sheet" onSubmit={submit} style={{ ["--cal" as string]: cal?.color ?? "var(--accent)" }}>
        <h2 id={`${id}-h`} className="sr-only">
          {editing ? t("ev.edit") : t("ev.new")}
        </h2>
        <div className="ev-top">
          <span className="ev-swatch" aria-hidden="true" />
          <input
            className="title-input"
            id={`${id}-title`}
            value={d.title}
            onChange={(e) => set("title", e.target.value)}
            placeholder={t("ev.titlePlaceholder")}
            aria-label={t("ev.titlePlaceholder")}
            autoFocus
            maxLength={500}
          />
          <button type="button" className="icon-btn" aria-label={t("ev.close")} onClick={onClose}>
            <svg className="icon" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </div>

        <div className="field">
          <label htmlFor={`${id}-cal`}>{t("cal.calendar")}</label>
          <div className="cal-select">
            <span className="dot" aria-hidden="true" />
            <select id={`${id}-cal`} className="input" value={d.calendarId} disabled={!!editing} onChange={(e) => set("calendarId", e.target.value)}>
              {writable.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                  {c.name !== c.connection.label ? ` · ${c.connection.label}` : ""}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="fld">
          <div className="when">
            <label className="switch-label">
              <input type="checkbox" checked={d.allDay} onChange={(e) => set("allDay", e.target.checked)} />
              <span className="sw" aria-hidden="true" />
              {t("common.allDay")}
            </label>
            <span className="muted tz">{tz.replace(/_/g, " ")}</span>
          </div>
          <div className="when-row">
            <span className="lb">{t("ev.starts")}</span>
            <input className="input input-sm" type="date" aria-label={t("ev.starts")} value={d.startDate} onChange={(e) => e.target.value && moveStart(e.target.value, d.startTime)} required />
            {!d.allDay && (
              <input className="input input-sm" type="time" step={300} aria-label={t("ev.starts")} value={d.startTime} onChange={(e) => e.target.value && moveStart(d.startDate, e.target.value)} required />
            )}
          </div>
          <div className="when-row">
            <span className="lb">{t("ev.ends")}</span>
            <input className="input input-sm" type="date" aria-label={t("ev.ends")} value={d.endDate} min={d.startDate} onChange={(e) => e.target.value && set("endDate", e.target.value)} required />
            {!d.allDay && <input className="input input-sm" type="time" step={300} aria-label={t("ev.ends")} value={d.endTime} onChange={(e) => e.target.value && set("endTime", e.target.value)} required />}
          </div>
          {invalid && <p className="field-error">{t("ev.endBeforeStart")}</p>}
          {conflicts.length > 0 && (
            <p className="alert alert-warn">{t("ev.conflict", { list: conflicts.map((c) => `${c.title || t("cal.noTitle")} (${timeFmt(c.start, locale, tz)})`).join(", ") })}</p>
          )}
        </div>

        {!editing?.recurring ? (
          <RepeatField d={d} set={set} />
        ) : scope === "this" ? (
          <p className="muted small">{t("ev.repeat.locked")}</p>
        ) : rule.data?.editable ? (
          <RepeatField d={d} set={set} />
        ) : rule.data ? (
          <p className="muted small">{t("ev.repeat.custom")}</p>
        ) : null}

        <PlaceField label={t("ev.location")} value={d.location} onChange={(v) => set("location", v)} />

        {provider === "google" || provider === "microsoft" ? (
          editing?.meetingUrl ? (
            <p className="small">
              {t("ev.videoLink")} :{" "}
              <a href={editing.meetingUrl} target="_blank" rel="noreferrer">
                {editing.meetingUrl}
              </a>
            </p>
          ) : (
            <label className="toggle">
              <input type="checkbox" checked={d.meeting} onChange={(e) => set("meeting", e.target.checked)} />
              <span className="sw" aria-hidden="true" />
              <span>{t(provider === "google" ? "ev.meet" : "ev.teams")}</span>
            </label>
          )
        ) : null}

        <GuestsField value={d.attendees} onChange={(v) => set("attendees", v)} />

        <div className="grid2">
          <div className="field">
            <label htmlFor={`${id}-busy`}>{t("ev.availability")}</label>
            <select id={`${id}-busy`} className="input" value={d.transparency} onChange={(e) => set("transparency", e.target.value as Draft["transparency"])}>
              <option value="opaque">{t("ev.busy")}</option>
              <option value="transparent">{t("ev.freeOption")}</option>
            </select>
          </div>
          <div className="field">
            <label htmlFor={`${id}-rem`}>{t("ev.reminder")}</label>
            <select id={`${id}-rem`} className="input" value={d.reminder} onChange={(e) => set("reminder", e.target.value)}>
              <option value="">{t("ev.reminder.default")}</option>
              <option value="0">{t("ev.reminder.atStart")}</option>
              {[5, 10, 15, 30].map((m) => (
                <option key={m} value={m}>
                  {t("ev.reminder.minutes", { n: String(m) })}
                </option>
              ))}
              <option value="60">{t("ev.reminder.hour")}</option>
              <option value="120">{t("ev.reminder.2hours")}</option>
              <option value="1440">{t("ev.reminder.day")}</option>
            </select>
          </div>
        </div>

        <label className="check">
          <input type="checkbox" checked={d.isPrivate} onChange={(e) => set("isPrivate", e.target.checked)} />
          <span>
            {t("ev.privateToggle")} <small className="muted">· {t("ev.privateHint")}</small>
          </span>
        </label>

        <div className="field">
          <label htmlFor={`${id}-desc`}>{t("ev.description")}</label>
          <textarea id={`${id}-desc`} className="input" rows={3} value={d.description} onChange={(e) => set("description", e.target.value)} maxLength={20_000} />
        </div>

        {editing?.recurring && <ScopeField value={scope} onChange={setScope} />}
        {error && <p className="alert alert-error">{error}</p>}

        <div className="row-end">
          <button type="button" className="btn" onClick={onClose}>
            {t("common.cancel")}
          </button>
          <button className="btn btn-primary" disabled={pending || invalid || !d.calendarId}>
            {pending ? t("ev.saving") : editing ? t("common.save") : t("ev.create")}
          </button>
        </div>
      </form>
    </dialog>
  );
}

const daysDiff = (a: DayKey, b: DayKey) => Math.round((new Date(`${b}T00:00:00Z`).getTime() - new Date(`${a}T00:00:00Z`).getTime()) / 86_400_000);

export function ScopeField({ value, onChange }: { value: EventScope; onChange: (s: EventScope) => void }) {
  const { t } = useI18n();
  return (
    <fieldset className="choices scope">
      <legend>{t("ev.scope")}</legend>
      {EVENT_SCOPES.map((s) => (
        <label key={s} className="radio-label">
          <input type="radio" checked={value === s} onChange={() => onChange(s)} />
          {t(`ev.scope.${s}`)}
        </label>
      ))}
    </fieldset>
  );
}

function RepeatField({ d, set }: { d: Draft; set: <K extends keyof Draft>(k: K, v: Draft[K]) => void }) {
  const { t, locale } = useI18n();
  const id = useId();
  return (
    <div className="fld">
      <label className="lb" htmlFor={`${id}-r`}>
        {t("ev.repeat")}
      </label>
      <div className="inline">
        <select id={`${id}-r`} className="input input-sm" value={d.repeat} onChange={(e) => set("repeat", e.target.value as Repeat)}>
          {(["none", "daily", "weekly", "monthly", "yearly"] as const).map((r) => (
            <option key={r} value={r}>
              {t(`ev.repeat.${r}`)}
            </option>
          ))}
        </select>
        {d.repeat !== "none" && (
          <>
            <span className="muted">{t("ev.repeat.every")}</span>
            <input className="input input-sm num" type="number" min={1} max={99} value={d.interval} aria-label={t("ev.repeat.every")} onChange={(e) => set("interval", Math.max(1, Math.min(99, Number(e.target.value) || 1)))} />
            <span className="muted">{t(`ev.repeat.unit.${d.repeat}`)}</span>
          </>
        )}
      </div>
      {d.repeat === "weekly" && (
        <div className="daychips" role="group" aria-label={t("ev.repeat.on")}>
          {WEEKDAYS.map((w, i) => {
            const on = d.byDay.includes(w);
            // 5 janvier 2026 : un lundi.
            const label = dayFmt(addDays("2026-01-05", i), locale, { weekday: "narrow" });
            return (
              <button
                key={w}
                type="button"
                aria-pressed={on}
                aria-label={dayFmt(addDays("2026-01-05", i), locale, { weekday: "long" })}
                onClick={() => set("byDay", on ? (d.byDay.length > 1 ? d.byDay.filter((x) => x !== w) : d.byDay) : WEEKDAYS.filter((x) => x === w || d.byDay.includes(x)))}
              >
                {label}
              </button>
            );
          })}
        </div>
      )}
      {d.repeat !== "none" && (
        <div className="inline">
          <span className="muted">{t("ev.repeat.ends")}</span>
          <select className="input input-sm" value={d.ends} aria-label={t("ev.repeat.ends")} onChange={(e) => set("ends", e.target.value as Draft["ends"])}>
            <option value="never">{t("ev.repeat.never")}</option>
            <option value="until">{t("ev.repeat.onDate")}</option>
            <option value="count">{t("ev.repeat.after")}</option>
          </select>
          {d.ends === "until" && <input className="input input-sm" type="date" min={d.startDate} value={d.until} aria-label={t("ev.repeat.onDate")} onChange={(e) => e.target.value && set("until", e.target.value)} />}
          {d.ends === "count" && (
            <>
              <input className="input input-sm num" type="number" min={1} max={730} value={d.count} aria-label={t("ev.repeat.times")} onChange={(e) => set("count", Math.max(1, Math.min(730, Number(e.target.value) || 1)))} />
              <span className="muted">{t("ev.repeat.times")}</span>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ───── Invités ─────

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function GuestsField({ value, onChange }: { value: string[]; onChange: (v: string[]) => void }) {
  const { t } = useI18n();
  const id = useId();
  const [text, setText] = useState("");
  const add = () => {
    const emails = text
      .split(/[\s,;]+/)
      .map((s) => s.trim().toLowerCase())
      .filter((s) => EMAIL.test(s) && !value.includes(s));
    if (emails.length) onChange([...value, ...emails]);
    if (emails.length || !text.trim()) setText("");
  };
  return (
    <div className="field">
      <label htmlFor={`${id}-g`}>{t("ev.guests")}</label>
      {value.length > 0 && (
        <ul className="chips">
          {value.map((email) => (
            <li key={email}>
              {email}
              <button type="button" aria-label={t("ev.removeGuest", { email })} onClick={() => onChange(value.filter((x) => x !== email))}>
                ×
              </button>
            </li>
          ))}
        </ul>
      )}
      <input
        id={`${id}-g`}
        className="input"
        type="email"
        multiple
        value={text}
        placeholder={t("ev.guestsPlaceholder")}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === "," || e.key === ";") {
            e.preventDefault();
            add();
          }
        }}
        onBlur={add}
      />
      {value.length > 0 && <small className="field-hint">{t("ev.invitesNote")}</small>}
    </div>
  );
}

// ───── Conflits ─────

function useConflicts(range: { allDay: boolean; start: string; end: string }, ignoreId: string | undefined, calendars: Map<string, CalendarInfo>, free: boolean) {
  const from = range.allDay ? `${range.start}T00:00:00Z` : range.start;
  const to = range.allDay ? `${range.end}T00:00:00Z` : range.end;
  const valid = !Number.isNaN(Date.parse(from)) && !Number.isNaN(Date.parse(to)) && to > from;
  const q = useQuery({
    queryKey: ["events", "conflicts", from, to],
    queryFn: async () => (await get<{ events: EventView[] }>(`/api/events?from=${new Date(from).toISOString()}&to=${new Date(to).toISOString()}`)).events,
    enabled: valid && !free && !range.allDay,
    staleTime: 30_000,
  });
  if (free || range.allDay) return [];
  return (q.data ?? []).filter(
    (e) => e.id !== ignoreId && !e.allDay && e.transparency === "opaque" && !e.declined && calendars.get(e.calendarId)?.countsAsBusy && e.start < to && e.end > from,
  );
}

// ───── Suppression ─────

export function DeleteBar({ ev, onDone, onCancel }: { ev: EventView; onDone: (message: string) => void; onCancel: () => void }) {
  const { t, errorText } = useI18n();
  const qc = useQueryClient();
  const [scope, setScope] = useState<EventScope>("this");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const confirm = async () => {
    setPending(true);
    setError(null);
    try {
      await del(`/api/events/${ev.id}?scope=${ev.recurring ? scope : "this"}`);
      await qc.invalidateQueries({ queryKey: ["events"] });
      onDone(t("ev.deleted"));
    } catch (err) {
      setError(writeErrorText(err, errorText));
      setPending(false);
    }
  };
  return (
    <div className="delete-bar">
      <strong>{t("ev.confirmDelete")}</strong>
      {ev.recurring && <ScopeField value={scope} onChange={setScope} />}
      {ev.attendees.length > 0 && <small className="muted">{t("ev.deleteNotifies")}</small>}
      {error && <p className="alert alert-error">{error}</p>}
      <div className="row-end">
        <button type="button" className="btn btn-sm" onClick={onCancel}>
          {t("common.cancel")}
        </button>
        <button type="button" className="btn btn-sm btn-danger" onClick={confirm} disabled={pending}>
          {t("ev.delete")}
        </button>
      </div>
    </div>
  );
}

